import { inject, observer } from 'mobx-react'
import { useTheme } from '@emotion/react'

import { withRouterProps } from '../../../utils'
import { SVGWrapper } from '../../../components'
import { PiggyBank2, Account, Target, Investment, Profile, Wallet } from '../../../assets/icons'
import { usesDefaultSalary } from './useActionItems'
import {
  QuickActionsSection,
  SectionHeading,
  QuickActionsGrid,
  QuickActionCard,
  QuickActionIcon,
  QuickActionLabel,
  Chevron,
} from './styles'

const MAX_ACTIONS = 3

const QuickActions = ({
  navigate,
  store: {
    account,
    baseCase,
    institutional,
    features,
    config: { isRetail },
  },
}) => {
  const theme = useTheme()
  const hasUnrestrictedCompanyStock = (baseCase?.companyStocks ?? []).some(
    stock => !stock.restricted
  )

  const changeSavingsFirst =
    features.enableSavingsRateUpdate && institutional.rateChangeAllowed && !isRetail

  /*
   * The salary of record is still our $10,000 placeholder, so the advice the
   * user is looking at is built on a number nobody meant. Sending them to the
   * profile to fix it outranks everything else here, and it displaces "Update
   * personal info" rather than sitting alongside it — same destination, and the
   * pair would spend two of the three slots saying "go to your profile".
   * The matching action item spells out why (see ActionItems).
   */
  const reviewIncome = usesDefaultSalary(account)

  const priorityActions = [
    changeSavingsFirst && {
      key: 'change-savings',
      label: "Change how much I'm saving only",
      icon: PiggyBank2,
      onClick: () => navigate('/update-contributions-only'),
    },
    hasUnrestrictedCompanyStock && {
      key: 'sell-company-stock',
      label: 'Sell company stock',
      icon: Investment,
      onClick: () => navigate('/advice/company-stock', { state: { from: '/home' } }),
    },
  ].filter(Boolean)

  const universalActions = [
    {
      key: 'add-accounts',
      label: 'Add accounts',
      icon: Account,
      onClick: () => navigate('/accounts'),
    },
    { key: 'add-goals', label: 'Add other goals', icon: Target, onClick: () => navigate('/goals') },
    !reviewIncome && {
      key: 'update-personal-info',
      label: 'Update personal info',
      icon: Profile,
      onClick: () => navigate('/profile'),
    },
  ].filter(Boolean)

  const actions = [
    // Ahead of the conditional actions too: this is the one card saying the
    // numbers the rest of the screen is built on are wrong.
    reviewIncome && {
      key: 'review-household-income',
      label: 'Review household income',
      icon: Wallet,
      onClick: () => navigate('/profile'),
    },
    ...priorityActions,
    ...universalActions,
  ]
    .filter(Boolean)
    .slice(0, MAX_ACTIONS)

  return (
    <QuickActionsSection>
      <SectionHeading>Quick actions</SectionHeading>
      <QuickActionsGrid>
        {actions.map(action => (
          <QuickActionCard key={action.key} onClick={action.onClick}>
            <QuickActionIcon>
              <SVGWrapper
                svg={action.icon}
                fill={theme.primaryColor}
                size='custom30'
                cursor='pointer'
              />
            </QuickActionIcon>
            <QuickActionLabel>{action.label}</QuickActionLabel>
            <Chevron />
          </QuickActionCard>
        ))}
      </QuickActionsGrid>
    </QuickActionsSection>
  )
}

export default withRouterProps(inject('store')(observer(QuickActions)))
